import React, { useEffect, useCallback } from "react";
import styles from "./ReelsList.module.css";
import { useAtom } from "jotai";
import { useCheckLogin } from "@/hooks";
import {
  detailListState,
  defaultDetailListState,
  detailListKeyowrd,
} from "../state";
import {
  BottomNavigationBar,
  useConfirm,
  SearchBar,
  Header,
  ThreeImageList,
} from "@components";
import Axios from "@/Axios";
import { useNavigate, useParams } from "react-router-dom";
import { INSERT_TAG } from "..";
import { decoded, encoded } from "@/utils";
import {
  useLoadReset,
  useDebouncedCallback,
  useDimLoading,
  useDimLoadComplete,
} from "@/hooks";

export default function ReelsList() {
  const dimLoading = useDimLoading();
  const dimLoadComplete = useDimLoadComplete();
  const loadReset = useLoadReset();
  const confirm = useConfirm();
  const [state, setState] = useAtom(detailListState);
  const [keyword, setKeyword] = useAtom(detailListKeyowrd);
  const checkLogin = useCheckLogin();
  const navigate = useNavigate();
  const { id, title } = useParams();

  const resetState = () => {
    setState(() => ({ ...defaultDetailListState }));
    loadReset();
  };
  const turnIntoUpdate = () => {
    setState((s) => ({
      ...s,
      isUpdate: true,
    }));
  };
  const updateToNormal = () => {
    setState((s) => ({
      ...s,
      isUpdate: false,
    }));
  };
  const turnIntoDelete = () => {
    setState((s) => ({
      ...s,
      isDelete: true,
      isReset: false,
      isDim: true,
    }));
  };
  const deleteToNormal = () => {
    setState((s) => ({
      ...s,
      isDelete: false,
      isReset: true,
      isDim: false,
    }));
  };
  const getAllList = () => {
    Axios.get("/api/reels/list", { params: { reelsCategoryId: id } }).then(
      (res) => {
        if (res.data?.errMsg) {
          confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
          return;
        }
        const count = res.data?.count || 0;
        if (!res.data?.reelsLists) {
          setState((s) => ({ ...s, list: [], count }));
          return;
        }
        const list = res.data?.reelsLists.map((data) => ({
          ...data,
          id: data.index,
          title: data.reelsNm,
        }));
        setState((s) => ({ ...s, list, count }));
      }
    );
  };

  const getList = useDebouncedCallback(() => {
    dimLoading();
    Axios.get("/api/reels/list-search", {
      params: { keyword, reelsCategoryId: id },
    }).then((res) => {
      if (res.data?.errMsg) {
        dimLoadComplete(() => {
          confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
        });
        return;
      }
      const count = res.data?.count || 0;
      if (!res.data?.reelsLists) {
        setState((s) => ({ ...s, list: [], count }));
        dimLoadComplete();
        return;
      }
      const list = res.data?.reelsLists.map((data) => ({
        ...data,
        id: data.index,
        title: data.reelsNm,
      }));
      setState((s) => ({ ...s, list, count }));
      dimLoadComplete();
    });
  });

  const keywordSearch = (e) => {
    setKeyword(e.target.value);
    if (e.key === "Enter") {
      getList();
      e.target.blur();
    }
  };

  const toInsert = () => {
    const info = encoded(
      {
        title: title,
        reelsCategoryId: id,
      },
      true
    );
    navigate(`/reels-upsert/${INSERT_TAG}/${info}`);
  };

  const deleteInfo = useDebouncedCallback(() => {
    confirm("정말로 삭제하시겠습니까?", () => {
      if (!state.deletes?.length) {
        confirm("삭제할 대상이 존재하지 않습니다.");
        return;
      }

      dimLoading();
      Axios.post("/api/reels/delete", {
        reelsLists: state.deletes,
      }).then((res) => {
        if (res.data?.errMsg) {
          dimLoadComplete(() => {
            confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
          });
          return;
        }
        setState((s) => ({
          ...defaultDetailListState,
          isLogin: s.isLogin,
          title: s.title,
        }));
        getAllList();
        dimLoadComplete(() => {
          confirm("삭제를 완료했습니다.");
        });
      });
    });
  });

  const setDeletes = useCallback((d) => {
    setState((s) => ({ ...s, deletes: [...s?.deletes, d] }));
  }, []);

  const resetDeletes = useCallback((d) => {
    setState((s) => ({
      ...s,
      deletes: [...s?.deletes].filter((obj) => obj.index !== d.index),
    }));
  }, []);

  const decodedTitle = () => {
    const newTitle = decoded(title);
    setState((s) => ({ ...s, title: newTitle }));
  };

  // 최초 한번 실행행
  useEffect(() => {
    getAllList();
    const callbackId = checkLogin(
      () => {
        setState((s) => ({ ...s, isLogin: true }));
      },
      () => {
        setState((s) => ({ ...s, isLogin: false }));
      }
    );
    // 타이틀 정보 디코딩
    decodedTitle();

    return () => {
      cancelIdleCallback(callbackId);
      resetState();
    };
  }, []);
  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.titleSearch}>
            <Header title={state.title} />
            <SearchBar
              keywordSearch={keywordSearch}
              placeholder={"제목을 입력해주세요"}
            />
            {state.isDim && <div className={styles.topDim} />}
          </div>
          <div className={styles.contentFooter}>
            <div className={styles.content}>
              <div className={styles.subBtn}>
                <span className={styles.category}>
                  총({state.count ? state.count.toLocaleString() : "0"}개)
                </span>
                {state.isLogin && !state.isUpdate && !state.isDelete && (
                  <>
                    <button className={styles.updateBtn} onClick={toInsert}>
                      <span className={styles.update}>등록</span>
                    </button>
                    <button
                      className={styles.updateBtn}
                      onClick={turnIntoUpdate}
                    >
                      <span className={styles.update}>수정</span>
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={turnIntoDelete}
                    >
                      <span className={styles.delete}>삭제</span>
                    </button>
                  </>
                )}
                {state.isUpdate && (
                  <>
                    <div className={styles.deleting}>
                      <span className={styles.deleting1}>수정중...</span>
                    </div>
                    <div className={styles.deleting}></div>
                    <button
                      className={styles.cancelBtn}
                      onClick={updateToNormal}
                    >
                      <span className={styles.cancel}>취소</span>
                    </button>
                  </>
                )}
                {state.isDelete && (
                  <>
                    <div className={styles.deleting}>
                      <span className={styles.deleting1}>삭제중...</span>
                    </div>
                    <button className={styles.deleteBtn} onClick={deleteInfo}>
                      <span className={styles.delete}>삭제</span>
                    </button>
                    <button
                      className={styles.cancelBtn}
                      onClick={deleteToNormal}
                    >
                      <span className={styles.cancel}>취소</span>
                    </button>
                  </>
                )}
              </div>
              <ThreeImageList
                list={state.list}
                defaultUrl={"/reels-content"}
                updateUrl={"/reels-upsert"}
                isUpdate={state.isUpdate}
                isDelete={state.isDelete}
                isReset={state.isReset}
                setDeletes={setDeletes}
                resetDeletes={resetDeletes}
                headerTitle={title}
              />
            </div>
          </div>
        </div>
        <div className={styles.footerContainer}>
          <BottomNavigationBar />
          {state.isDim && <div className={styles.bottomDimmed} />}
        </div>
      </div>
    </div>
  );
}
